<template>
  <div class="login-wrapper responsive-p">
    <div class="login-box" :class="{ 'nf-bg': notFoundPane }">
      <!-- Not-found nav bar (replaces the default title row) -->
      <template v-if="notFoundPane">
        <div class="nf-header">
          <button class="nf-back" type="button" @click="nfBack" aria-label="ย้อนกลับ" v-if="nfStep !== 'done'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"></path></svg>
          </button>
          <span v-else></span>
          <span class="nf-header-title">{{ nfHeaderTitle }}</span>
          <span></span>
        </div>
      </template>
      <!-- Step-1 back (exits the flow) hidden to keep users in registration.
           Step-2 back is kept so a mistyped phone can still be corrected. -->
      <p class="back" @click="back" v-if="step === 2 && !notFoundPane">&lt; {{ t('back') }}</p>
      <p class="title" v-if="!notFoundPane">{{ t('verifyUser.title') }}</p>
      <div class="log-in-with-phone" v-if="step === 1 && !notFoundPane">
        <p class="description">
          {{ t('verifyUser.pleaseFillPhone') }}<br/>
          {{ t('verifyUser.forVerify') }}
        </p>
        <p class="description">
          {{ t('verifyUser.getFree') }} <img src="@/assets/icons/m-coin.svg"> {{ t('verifyUser.coins', [100]) }}
        </p>
        <TextInput v-model="phone" :label="t('phone')" type="tel" digits-only :max-length="10" />
        <DropdownInput
          label="สาขาที่ใช้บริการล่าสุด"
          v-model="selectedBranch"
          name="branch"
          :items="branches"
          :loading="branchesLoading"
          :placeholder="t('selectPlaceholder')"
          :loading-text="t('loadingOptions')"
          :empty-text="t('noOptionsRetry')"
          @retry="loadBranches"
        />
        <MButton @click="startPhoneFlow">{{ t('getOTP') }}</MButton>
      </div>
      <div class="input-otp" v-else-if="step === 2 && !notFoundPane">
        <p class="description">
          {{ t('verifyUser.pleaseFillOtp') }}
        </p>
        <TextInput autofocus v-model="otp" :label="t('otp')" />
        <MButton @click="verifyOTP">{{ t('confirm') }}</MButton>
        <p v-if="second > 0" class="didnt-get-otp mt-2">{{  t('didntGetOtp') }} ({{ t('otpCounter', { second }) }})</p>
        <p @click="getOTP" class="resend-otp mt-2" v-if="second === 0">{{  t('resendOtp') }}</p>
      </div>
      <!-- Step: phone not found -->
      <div class="nf-pane" v-if="nfStep === 'notfound'">
        <div class="nf-warn" aria-hidden="true">!</div>
        <h2 class="nf-title">ไม่พบเบอร์นี้ในระบบคนไข้</h2>
        <div class="nf-phone">{{ formatPhone(notFoundPhone) }}</div>
        <p class="nf-hint">หากกรอกเบอร์ผิด <button class="nf-link" type="button" @click="retryPhone">แก้ไขเบอร์และตรวจสอบอีกครั้ง</button></p>
        <p class="nf-question">ขณะนี้คุณอยู่ที่สาขากับเจ้าหน้าที่หรือไม่</p>
        <div class="nf-choices">
          <button class="nf-choice" type="button" @click="goAtBranch">
            <b>ใช่ อยู่ที่สาขากับเจ้าหน้าที่</b>
            <span>เจ้าหน้าที่จะอัปเดตเบอร์ให้ที่สาขา</span>
          </button>
          <button class="nf-choice" type="button" @click="goOnline">
            <b>ไม่ได้อยู่ที่สาขา</b>
            <span>ดำเนินการต่อทางออนไลน์</span>
          </button>
        </div>
      </div>
      <!-- Step: at branch — show phone to staff -->
      <div class="nf-pane" v-if="nfStep === 'at-branch'">
        <div class="nf-done-mark" aria-hidden="true">i</div>
        <h2 class="nf-title nf-center">กรุณาแจ้งเจ้าหน้าที่สาขา</h2>
        <p class="nf-body nf-center">แจ้งเจ้าหน้าที่ว่าต้องการอัปเดตเบอร์โทรศัพท์ในระบบคนไข้เป็นเบอร์นี้</p>
        <div class="nf-phone nf-phone-big">{{ formatPhone(notFoundPhone) }}</div>
        <p class="nf-body nf-center">เมื่อเจ้าหน้าที่อัปเดตเรียบร้อยแล้ว ระบบจะเชื่อมประวัติ Makalin Connect ให้อัตโนมัติ และแจ้งผลทาง LINE คุณไม่ต้องทำรายการซ้ำ</p>
        <button class="nf-btn" type="button" @click="retryPhone">ปิดหน้านี้</button>
      </div>
      <!-- Step: not at branch — which problem -->
      <div class="nf-pane" v-if="nfStep === 'problem'">
        <h2 class="nf-title">คุณมีปัญหาการเชื่อมประวัติแบบใด</h2>
        <p class="nf-body">เลือกข้อที่ตรงกับคุณ</p>
        <div class="nf-choices">
          <button class="nf-choice" type="button" @click="goOnlineNewPatient">
            <b>ยังไม่เคยมีประวัติคนไข้ที่คลินิก</b>
            <span>ลงทะเบียนเป็นคนไข้ใหม่</span>
          </button>
          <button class="nf-choice" type="button" @click="goOnlineChangePhone" :disabled="checkingPhone">
            <b>เบอร์ปัจจุบันไม่ตรงกับเบอร์เดิมในระบบ</b>
            <span>{{ checkingPhone ? 'กำลังตรวจสอบ…' : 'ไม่แน่ใจว่าเคยใช้เบอร์ไหน ก็เลือกข้อนี้ได้' }}</span>
          </button>
        </div>
      </div>
      <!-- Step: pending — existing request in review -->
      <div class="nf-pane" v-if="nfStep === 'pending'">
        <h2 class="nf-title">คำขออยู่ระหว่างตรวจสอบ</h2>
        <span class="nf-pill">รอตรวจสอบ</span>
        <dl class="nf-kv" v-if="pendingInfo">
          <dt>เลขที่คำขอ</dt><dd>{{ pendingInfo.request_id }}</dd>
          <dt>ส่งเมื่อ</dt><dd>{{ formatPendingDate(pendingInfo.created_at) }}</dd>
          <dt>เบอร์ใหม่</dt><dd>{{ formatPhone(notFoundPhone) }}</dd>
        </dl>
        <p class="nf-body">ไม่ต้องส่งคำขอซ้ำ ระบบจะเชื่อมประวัติให้อัตโนมัติและแจ้งผลทาง LINE นี้ หากต้องการแก้ไขข้อมูล กรุณาพิมพ์แจ้งในแชท</p>
        <button class="nf-btn" type="button" @click="retryPhone">ปิดหน้านี้</button>
      </div>
      <!-- Step: intro — เชื่อมประวัติด้วยเบอร์ใหม่ -->
      <div class="nf-pane" v-if="nfStep === 'intro'">
        <h2 class="nf-title">เชื่อมประวัติด้วยเบอร์ใหม่</h2>
        <p class="nf-body">สำหรับคนไข้ที่เคยใช้บริการคลินิก Makalin แต่เชื่อมประวัติ Makalin Connect ไม่สำเร็จ เนื่องจากเบอร์โทรศัพท์ปัจจุบันไม่ตรงกับเบอร์ที่บันทึกไว้ในระบบคนไข้</p>
        <div class="nf-hero-card">
          <small>ขออัปเดตเบอร์โทรศัพท์</small>
          <b>ใช้เวลากรอกประมาณ 2 นาที</b>
          <span>กรอกครั้งเดียว ระบบจะเชื่อมประวัติให้อัตโนมัติเมื่อเจ้าหน้าที่อัปเดตข้อมูลแล้ว</span>
        </div>
        <ol class="nf-steps-list">
          <li><span class="nf-step-n">1</span><div>กรอกข้อมูลคนไข้</div></li>
          <li><span class="nf-step-n">2</span><div>เจ้าหน้าที่ตรวจสอบและอัปเดตเบอร์ในระบบคนไข้</div></li>
          <li><span class="nf-step-n">3</span><div>รอผลการเชื่อมประวัติอัตโนมัติทาง LINE</div></li>
        </ol>
        <div class="nf-prep"><b>ข้อมูลที่ต้องใช้ &nbsp;</b>ชื่อและนามสกุลที่เคยลงทะเบียนไว้กับคลินิก (รวมชื่อหรือนามสกุลเดิม หากเคยเปลี่ยน), วันเดือนปีเกิด และเบอร์เดิม (หากจำได้)</div>
        <button class="nf-btn" type="button" @click="goForm">เริ่มกรอกข้อมูล</button>
        <p class="nf-body nf-center" style="font-size:.82rem">ยังไม่เคยใช้บริการที่คลินิก? <button class="nf-link" type="button" @click="goOnlineNewPatient">ลงทะเบียนคนไข้ใหม่</button></p>
      </div>
      <!-- Step: form — patient info form -->
      <div class="nf-pane nf-pane-form" v-if="nfStep === 'form'">
        <div class="nf-sec-title">ข้อมูลคนไข้</div>
        <div class="nf-field" :class="{ 'nf-field-invalid': formErrors.fname }">
          <label class="nf-label" for="nf-fname">ชื่อปัจจุบัน<span class="nf-req">*</span></label>
          <input id="nf-fname" class="nf-input" type="text" v-model="formFname" autocomplete="given-name" placeholder="ชื่อจริง ไม่ต้องใส่คำนำหน้า">
          <span class="nf-err" v-if="formErrors.fname">{{ formErrors.fname }}</span>
        </div>
        <label class="nf-check"><input type="checkbox" v-model="formChangedFn"> เคยเปลี่ยนชื่อหลังจากเริ่มใช้บริการ</label>
        <div class="nf-field nf-sub-field" v-if="formChangedFn" :class="{ 'nf-field-invalid': formErrors.oldfname }">
          <label class="nf-label" for="nf-oldfname">ชื่อเดิม<span class="nf-req">*</span></label>
          <input id="nf-oldfname" class="nf-input" type="text" v-model="formOldFname" placeholder="ชื่อที่เคยลงทะเบียนไว้กับคลินิก">
          <span class="nf-err" v-if="formErrors.oldfname">{{ formErrors.oldfname }}</span>
        </div>
        <div class="nf-field" :class="{ 'nf-field-invalid': formErrors.lname }">
          <label class="nf-label" for="nf-lname">นามสกุลปัจจุบัน<span class="nf-req">*</span></label>
          <input id="nf-lname" class="nf-input" type="text" v-model="formLname" autocomplete="family-name">
          <span class="nf-err" v-if="formErrors.lname">{{ formErrors.lname }}</span>
        </div>
        <label class="nf-check"><input type="checkbox" v-model="formChangedLn"> เคยเปลี่ยนนามสกุลหลังจากเริ่มใช้บริการ</label>
        <div class="nf-field nf-sub-field" v-if="formChangedLn" :class="{ 'nf-field-invalid': formErrors.oldlname }">
          <label class="nf-label" for="nf-oldlname">นามสกุลเดิม<span class="nf-req">*</span></label>
          <input id="nf-oldlname" class="nf-input" type="text" v-model="formOldLname" placeholder="นามสกุลที่เคยลงทะเบียนไว้กับคลินิก">
          <span class="nf-err" v-if="formErrors.oldlname">{{ formErrors.oldlname }}</span>
        </div>
        <div class="nf-field" :class="{ 'nf-field-invalid': formErrors.dob }">
          <span class="nf-label">วันเดือนปีเกิด<span class="nf-req">*</span></span>
          <div class="nf-row3">
            <select class="nf-select" v-model="formDobDay" aria-label="วัน">
              <option value="">วัน</option>
              <option v-for="d in dobDays" :key="d" :value="d">{{ d }}</option>
            </select>
            <select class="nf-select" v-model="formDobMonth" aria-label="เดือน">
              <option value="">เดือน</option>
              <option v-for="m in dobMonths" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
            <select class="nf-select" v-model="formDobYear" aria-label="ปี พ.ศ.">
              <option value="">ปี พ.ศ.</option>
              <option v-for="y in dobYears" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <span class="nf-err" v-if="formErrors.dob">{{ formErrors.dob }}</span>
        </div>
        <div class="nf-sec-title">เบอร์โทรศัพท์</div>
        <div class="nf-field" :class="{ 'nf-field-invalid': formErrors.oldphone }">
          <label class="nf-label" for="nf-oldphone">เบอร์เดิมที่เคยให้ไว้กับคลินิก<span class="nf-opt">ไม่บังคับ</span></label>
          <input id="nf-oldphone" class="nf-input" type="tel" v-model="formOldPhone" :disabled="formForgotOld" placeholder="0XX-XXX-XXXX" maxlength="10" inputmode="numeric">
          <span class="nf-err" v-if="formErrors.oldphone">{{ formErrors.oldphone }}</span>
        </div>
        <label class="nf-check"><input type="checkbox" v-model="formForgotOld"> จำเบอร์เดิมไม่ได้</label>
        <div class="nf-field">
          <span class="nf-label">เบอร์ใหม่ที่ต้องการอัปเดต</span>
          <div class="nf-phone-locked">
            <b>{{ formatPhone(notFoundPhone) }}</b>
            <span class="nf-verified">✓ ยืนยัน OTP แล้ว</span>
          </div>
          <span class="nf-help">เบอร์นี้ยืนยันด้วยรหัส OTP แล้วตั้งแต่ขั้นตอนเชื่อมประวัติ</span>
        </div>
        <div class="nf-sec-title">ความยินยอม</div>
        <div class="nf-field" :class="{ 'nf-field-invalid': formErrors.consent }">
          <label class="nf-check"><input type="checkbox" v-model="formConsent"> ข้าพเจ้ายินยอมให้คลินิก Makalin ใช้ข้อมูลข้างต้นเพื่อยืนยันตัวตน ปรับปรุงเบอร์โทรศัพท์ในระบบคนไข้ และเชื่อมประวัติ Makalin Connect ตามนโยบายความเป็นส่วนตัวของคลินิก</label>
          <span class="nf-err" v-if="formErrors.consent">{{ formErrors.consent }}</span>
        </div>
        <span class="nf-form-err" v-if="formErrors.general">{{ formErrors.general }}</span>
        <button class="nf-btn" type="button" @click="submitPhoneUpdateForm" :disabled="formSubmitting">ส่งคำขอ</button>
      </div>
      <!-- Step: done — request submitted -->
      <div class="nf-pane" v-if="nfStep === 'done'">
        <div class="nf-done-mark" aria-hidden="true">✓</div>
        <h2 class="nf-title nf-center">ได้รับคำขอของคุณแล้ว</h2>
        <div class="nf-phone nf-center" v-if="formResult?.ref">{{ formResult.ref }}</div>
        <p class="nf-body">เจ้าหน้าที่จะตรวจสอบและอัปเดตเบอร์โทรศัพท์ในระบบคนไข้ภายใน 1 วันทำการ เมื่ออัปเดตแล้ว ระบบจะเชื่อมประวัติ Makalin Connect ให้อัตโนมัติ และแจ้งผลทาง LINE นี้ คุณไม่ต้องทำรายการซ้ำ</p>
        <dl class="nf-kv" v-if="formResult?.rows">
          <template v-for="row in formResult.rows" :key="row[0]">
            <dt>{{ row[0] }}</dt><dd>{{ row[1] }}</dd>
          </template>
        </dl>
        <button class="nf-btn" type="button" @click="retryPhone">ปิดหน้านี้</button>
      </div>
      <div class="not-found-modal-wrapper" v-if="showNotFoundModal">
        <div class="card-wrapper">
          <p>
            {{ t('notFoundPhoneInSystem') }}
            {{ t('pleaseContactAddmin') }}
          </p>
          <div class="button-wrapper">
            <MButton outline @click="submitNotFound(false)">{{ t('cancel') }}</MButton>
            <MButton class="ml-2" @click="submitNotFound(true)">{{ t('confirm') }}</MButton>
          </div>
        </div>
      </div>
    </div>

    <SyncHistoryModal
      :visible="showSyncModal"
      :start-screen="syncStartScreen"
      :initial-phone="phone"
      :initial-branch-id="selectedBranchId"
      :require-line-account="false"
      success-path="/my-profile"
      success-label-text="ไปที่โปรไฟล์ของฉัน →"
      @close="showSyncModal = false"
      @success="showSyncModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import liff from '@line/liff'
import { useScreenSize } from '@/utils/isDesktop'

import MButton from '@/components/Form/MButton'
import TextInput from '@/components/Form/TextInput'
import DropdownInput from '@/components/Form/DropdownInput'
import SyncHistoryModal from '@/components/SyncHistoryModal'
import userApi from '@/api/user'
import { SYNC_FROM, syncRewardsPath, shouldRedirectToSyncRewards } from '@/utils/syncRewards'
import { isClinicCustomerNotFound } from '@/utils/apiErrors'
import { rememberPhone } from '@/utils/phoneMemory'
import { logEvent } from '@/utils/diagnostics'

export default {
  name: 'UserLogin',
  components: {
    MButton,
    TextInput,
    DropdownInput,
    SyncHistoryModal,
  },
  setup() {
    /** context */
    const store = useStore()
    const router = useRouter()
    const { t } = useI18n()
    const { isDesktop } = useScreenSize()

    /** reactive */
    const phone = ref('')
    const otp = ref('')
    const step = ref(1)
    const selectedBranch = ref({})
    const second = ref(0)
    const counter = ref(null)
    const showSyncModal = ref(false)
    const syncStartScreen = ref('')
    const branchesLoading = ref(false)
    const nfStep = ref('')
    const notFoundPane = computed(() => !!nfStep.value)
    const notFoundPhone = ref('')
    const formFname = ref('')
    const formOldFname = ref('')
    const formChangedFn = ref(false)
    const formLname = ref('')
    const formOldLname = ref('')
    const formChangedLn = ref(false)
    const formDobDay = ref('')
    const formDobMonth = ref('')
    const formDobYear = ref('')
    const formOldPhone = ref('')
    const formForgotOld = ref(false)
    const formConsent = ref(false)
    const formErrors = ref({})
    const formSubmitting = ref(false)
    const formResult = ref(null)
    const pendingInfo = ref(null)
    const checkingPhone = ref(false)
    const phoneNotInSystem = ref(false)
    const dobDays = Array.from({ length: 31 }, (_, i) => i + 1)
    const dobMonths = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'].map((label, i) => ({ value: i + 1, label }))
    const dobYears = Array.from({ length: 101 }, (_, i) => 2569 - i)
    /** computed */
    const showNotFoundModal = computed(() => store.state.user.notFound)
    const requestIdOtp = computed(() => store.state.user.requestIdOtp)
    const getUser = computed(() => store.getters['user/getUser'])
    const branches = computed(() => store.getters['user/getBranchOptions'])
    const selectedBranchId = computed(() => selectedBranch.value?.value ?? null)

    /** method */
    const redirectToSyncRewards = (err) => {
      if (!shouldRedirectToSyncRewards(err)) return false
      // The api interceptor may already have queued a snackbar for this error, and a
      // 404 also arms the "not found" modal. Clear both so neither follows the user
      // onto the next page.
      store.commit('snackBar/RESET_DATA')
      store.commit('user/SET_NOT_FOUND', false)
      router.push(syncRewardsPath(SYNC_FROM.userVerify))
      return true
    }
    const getOTP = async () => {
      otp.value = ''
      phoneNotInSystem.value = false
      try {
        await store.dispatch('user/fetchGetVerifyUserOTP', { phone: phone.value, branchId: selectedBranch.value.value })
      }
      catch (err) {
        if (isClinicCustomerNotFound(err?.detail)) {
          store.commit('snackBar/RESET_DATA')
          store.commit('user/SET_NOT_FOUND', false)
          notFoundPhone.value = phone.value
          phoneNotInSystem.value = true
          store.commit('user/SET_REQUEST_ID_OTP', 'NOT_FOUND')
          return
        }
        redirectToSyncRewards(err)
      }
    }
    const startPhoneFlow = async () => {
      if (!phone.value) return
      try {
        await getOTP()
      }
      catch (err) {
        if (redirectToSyncRewards(err)) return
        store.commit('snackBar/SET_DATA', { message: err?.detail || t('somethingWrong'), color: 'error' })
      }
    }
    const verifyOTP = async () => {
      if (!otp.value.trim()) {
        store.commit('snackBar/SET_DATA', { message: 'กรุณากรอกรหัส OTP', color: 'error' })
        return
      }
      if (phoneNotInSystem.value) {
        store.commit('user/SET_NOT_FOUND', false)
        store.commit('user/RESET_REQUEST_ID_OTP')
        nfStep.value = 'notfound'
        return
      }
      try {
        await store.dispatch('user/fetchVerifyUserOTP', otp.value)
      }
      catch (err) {
        if (isClinicCustomerNotFound(err?.detail)) {
          store.commit('snackBar/RESET_DATA')
          store.commit('user/SET_NOT_FOUND', false)
          notFoundPhone.value = phone.value
          nfStep.value = 'notfound'
          return
        }
        redirectToSyncRewards(err)
      }
    }
    const back = () => {
      phoneNotInSystem.value = false
      store.commit('user/RESET_REQUEST_ID_OTP')
    }
    const backToMyProfile = () => {
      store.commit('user/SET_SHOW_VERIFY', true)
      router.back()
    }
    const loadBranches = async () => {
      branchesLoading.value = true
      try {
        await store.dispatch('user/fetchGetBranches')
      }
      catch (error) {
        // Store already surfaces a snackbar; the dropdown's empty state offers retry.
      }
      finally {
        branchesLoading.value = false
      }
    }
    const formatPhone = (p) => {
      const d = String(p).replace(/\D/g, '')
      if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
      return p
    }
    const retryPhone = () => {
      nfStep.value = ''
      store.commit('user/RESET_REQUEST_ID_OTP')
    }
    const nfHeaderTitle = computed(() => {
      const map = { notfound: 'ไม่พบเบอร์ในระบบ', 'at-branch': 'ไม่พบเบอร์ในระบบ', problem: 'ไม่พบเบอร์ในระบบ', intro: 'เชื่อมประวัติด้วยเบอร์ใหม่', form: 'กรอกข้อมูล', done: 'ส่งคำขอเรียบร้อย', pending: 'สถานะคำขอ' }
      return map[nfStep.value] || 'ไม่พบเบอร์ในระบบ'
    })
    const nfBack = () => {
      const backMap = { 'at-branch': 'notfound', problem: 'notfound', pending: 'problem', intro: 'problem', form: 'intro' }
      const prev = backMap[nfStep.value]
      if (prev) nfStep.value = prev
      else retryPhone()
    }
    const goAtBranch = () => {
      nfStep.value = 'at-branch'
    }
    const goOnline = () => {
      nfStep.value = 'problem'
    }
    const goOnlineNewPatient = () => {
      router.push(syncRewardsPath(SYNC_FROM.userVerify))
    }
    const goOnlineChangePhone = async () => {
      checkingPhone.value = true
      try {
        const res = await userApi.checkPhoneUpdateRequest(String(notFoundPhone.value).replace(/\D/g, ''))
        if (res?.has_pending) {
          pendingInfo.value = res
          nfStep.value = 'pending'
        } else {
          nfStep.value = 'intro'
        }
      } catch {
        nfStep.value = 'intro'
      } finally {
        checkingPhone.value = false
      }
    }
    const formatPendingDate = (dateStr) => {
      if (!dateStr) return ''
      try { return new Date(dateStr).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) }
      catch { return dateStr }
    }
    const goForm = () => {
      nfStep.value = 'form'
    }
    const submitPhoneUpdateForm = async () => {
      formErrors.value = {}
      const e = {}
      if (!formFname.value.trim()) e.fname = 'กรุณากรอกชื่อปัจจุบัน'
      if (formChangedFn.value && !formOldFname.value.trim()) e.oldfname = 'กรุณากรอกชื่อเดิม'
      if (!formLname.value.trim()) e.lname = 'กรุณากรอกนามสกุลปัจจุบัน'
      if (formChangedLn.value && !formOldLname.value.trim()) e.oldlname = 'กรุณากรอกนามสกุลเดิม'
      if (!formDobDay.value || !formDobMonth.value || !formDobYear.value) e.dob = 'กรุณาเลือกวันเดือนปีเกิดให้ครบ'
      if (!formConsent.value) e.consent = 'กรุณายืนยันความยินยอมก่อนส่งคำขอ'
      if (Object.keys(e).length) { formErrors.value = e; return }
      formSubmitting.value = true
      try {
        const profile = await liff.getProfile().catch(() => ({ userId: '', displayName: '' }))
        const res = await userApi.submitPhoneUpdateRequest({
          line_user_id: profile.userId,
          line_display_name: profile.displayName,
          first_name: formFname.value.trim(),
          last_name: formLname.value.trim(),
          previous_first_name: formChangedFn.value ? formOldFname.value.trim() : '',
          previous_last_name: formChangedLn.value ? formOldLname.value.trim() : '',
          dob_day: String(formDobDay.value),
          dob_month: String(formDobMonth.value),
          dob_year: String(formDobYear.value),
          last_branch_id: selectedBranch.value?.value || '',
          old_phone: formForgotOld.value ? '' : String(formOldPhone.value).replace(/\D/g, ''),
          new_phone: String(notFoundPhone.value).replace(/\D/g, ''),
          otp_verified_at: new Date().toISOString(),
          consent: true,
        })
        const monthLabel = dobMonths[Number(formDobMonth.value) - 1]?.label || formDobMonth.value
        const dob = `${formDobDay.value} ${monthLabel} ${formDobYear.value}`
        const rows = [['ชื่อ-นามสกุล', `${formFname.value.trim()} ${formLname.value.trim()}`]]
        if (formChangedFn.value || formChangedLn.value) {
          const oldName = [formOldFname.value.trim(), formOldLname.value.trim()].filter(Boolean).join(' ')
          if (oldName) rows.push(['ชื่อ/นามสกุลเดิม', oldName])
        }
        rows.push(['วันเกิด', dob])
        if (selectedBranch.value?.label) rows.push(['สาขาล่าสุด', selectedBranch.value.label])
        rows.push(['เบอร์ใหม่', formatPhone(notFoundPhone.value)])
        formResult.value = { ref: res?.request_id || res?.id || '', rows }
        nfStep.value = 'done'
      } catch (err) {
        formErrors.value = { general: err?.detail || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' }
      } finally {
        formSubmitting.value = false
      }
    }

    const submitNotFound = (isConfirm) => {
      store.commit('user/SET_NOT_FOUND', false)
      if (isConfirm) {
        const isInClient = liff.isInClient()
        const message = `ติดต่อเพื่อขอเปลี่ยนเบอร์โทรศัพท์ สำหรับการยืนยันตัวตน`
        if (!isInClient) {
          const os = liff.getOS()
          console.log(os)
          if (os === 'android' || os === 'ios') {
            console.log(`https://line.me/R/oaMessage/${process.env.VUE_APP_LINE_AT_ID}/?${encodeURIComponent(message)}`, '_blank')
            window.open(`https://line.me/R/oaMessage/${process.env.VUE_APP_LINE_AT_ID}/?${encodeURIComponent(message)}`, '_blank')
          }
          else if (os === 'web') {
            window.open(process.env.VUE_APP_LINE_URL_OFFICIAL_ACCOUNT, '_blank')
          }
        }
        else if (isInClient) {
          window.open(`https://line.me/R/oaMessage/${process.env.VUE_APP_LINE_AT_ID}/?${encodeURIComponent(message)}`, '_blank')
        }
      }
    }

    onMounted(() => {
      loadBranches()
      store.commit('user/RESET_REQUEST_ID_OTP')
    })
    
    /** watch */
    // Keep the number so the sync-rewards flow can prefill it.
    watch(phone, rememberPhone)
    watchEffect(() => {
      if (requestIdOtp.value) {
        step.value = 2
        second.value = 60
      }
      else {
        second.value = 0
        step.value = 1
      }
    })
    watchEffect(() => {
      if (getUser.value) {
        store.dispatch('user/fetchGetUserProfile', '/my-profile')
      }
    })
    watchEffect(() => {
      if (second.value === 60) {
        counter.value = setInterval(() => {
          second.value--
        }, 1000)
      }
      if (second.value === 0 && counter.value) {
        clearInterval(counter.value)
      }
    })
    // Record when the user is actually shown an empty branch dropdown, with full
    // client context. rawStoreCount tells apart "no data" from "data present but not
    // mapped/rendered". Once per session.
    watch([branches, branchesLoading], ([list, loading]) => {
      if (!loading && (!list || list.length === 0)) {
        logEvent('branch_render_empty', {
          page: 'verify',
          rawStoreCount: (store.state.user.branches || []).length,
        }, { once: 'branch_render_empty' })
      }
    })

    return {
      t,
      getOTP,
      startPhoneFlow,
      verifyOTP,
      phone,
      otp,
      requestIdOtp,
      step,
      back,
      backToMyProfile,
      branches,
      selectedBranch,
      selectedBranchId,
      branchesLoading,
      loadBranches,
      second,
      showNotFoundModal,
      submitNotFound,
      showSyncModal,
      syncStartScreen,
      isDesktop,
      notFoundPane,
      notFoundPhone,
      nfStep,
      nfHeaderTitle,
      nfBack,
      formatPhone,
      retryPhone,
      goAtBranch,
      goOnline,
      goOnlineNewPatient,
      goOnlineChangePhone,
      goForm,
      submitPhoneUpdateForm,
      formFname,
      formOldFname,
      formChangedFn,
      formLname,
      formOldLname,
      formChangedLn,
      formDobDay,
      formDobMonth,
      formDobYear,
      formOldPhone,
      formForgotOld,
      formConsent,
      formErrors,
      formSubmitting,
      dobDays,
      dobMonths,
      dobYears,
      formResult,
      pendingInfo,
      checkingPhone,
      formatPendingDate,
    }
  }
}
</script>

<style lang="scss" scoped>

.login-wrapper {
  height: 100%;
  @media screen and (min-width: 780px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100% - 74px);
  }
}

.login-box {
  background-color: var(--background-color);
  background-image: url('@/assets/images/bg-logo.png');
  background-size: cover;
  background-repeat: no-repeat;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px 28px 60px;
  @media screen and (min-width: 780px) {
    height: auto;
    width: 100%;
    min-width: 500px;
    border-radius: 8px;
    padding: 24px 28px;
  }
}

.log-in-with-phone {
  padding: 0 40px;
}

button {
  width: 100%;
}

.back {
  color: var(--secondary-color);
  margin-bottom: 12px;
  font-size: 1.2rem;
  padding: 12px;
  cursor: pointer;
}

.description {
  color: var(--secondary-color);
  text-align: center;
  font-size: 1.6rem;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  > img {
    margin: 0 12px;
  }
}

.title {
  text-align: center;
  font-size: 1.6rem;
  margin-bottom: 24px;
  color: var(--secondary-color);
}

.didnt-get-otp {
  color: var(--secondary-color);
}
.resend-otp {
  color: var(--secondary-color);
  text-decoration: underline;
  cursor: pointer;
}

.nf-bg {
  background-color: #F0F3FE !important;
  background-image: none !important;
  font-family: "Sukhumvit Set","Noto Sans Thai",Tahoma,sans-serif;
  min-height: 100vh;
  @media screen and (min-width: 780px) {
    min-height: unset;
  }
}
.nf-header {
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 6px 8px 12px;
  border-bottom: 1px solid #DCE4EF;
  background: #FFFFFF;
  flex: none;
  margin: -24px -28px 0;
}
.nf-header-title {
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  color: #000000;
}
.nf-back {
  border: 0;
  background: none;
  color: #2E3AE6;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
  > svg {
    width: 20px;
    height: 20px;
  }
  &:focus-visible {
    outline: 2px solid #2E3AE6;
    outline-offset: 2px;
    border-radius: 4px;
  }
}

/* design tokens matching demo.html */
.nf-pane {
  --nf-blue: #2E3AE6;
  --nf-ink: #111111;
  --nf-muted: #6A6A6A;
  --nf-faint: #9A9A9A;
  --nf-line: #DCE4EF;
  --nf-tint: #F4F6FF;
  --nf-outline: #8E95EE;
  --nf-ui: "Sukhumvit Set","Noto Sans Thai",Tahoma,sans-serif;

  font-family: var(--nf-ui);
  font-size: 15px;
  padding: 20px 18px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.nf-warn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #FFF1E0;
  color: #E07A00;
  display: grid;
  place-items: center;
  font-size: 2rem;
  font-weight: 700;
  margin: 8px auto 0;
}
.nf-title {
  font-size: 1.45rem;
  line-height: 1.3;
  margin: 0;
  font-weight: 700;
  color: #000000;
  text-align: center;
}
.nf-phone {
  font-weight: 600;
  font-size: 1.05rem;
  background: #FFFFFF;
  border: 1px solid var(--nf-line);
  color: var(--nf-blue);
  border-radius: 14px;
  padding: 12px;
  text-align: center;
  letter-spacing: .02em;
}
.nf-hint {
  margin: 0;
  color: var(--nf-muted);
  font-size: .88rem;
  line-height: 1.55;
  text-align: center;
}
.nf-link {
  background: none;
  border: 0;
  padding: 0;
  color: var(--nf-blue);
  font: 500 .85rem var(--nf-ui);
  text-decoration: underline;
  cursor: pointer;
}
.nf-question {
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  padding-top: 8px;
  border-top: 1px solid var(--nf-line);
  margin: 0;
}
.nf-choices {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.nf-choice {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1.5px solid var(--nf-line);
  background: #FFFFFF;
  border-radius: 16px;
  padding: 14px 44px 14px 16px;
  cursor: pointer;
  font-family: var(--nf-ui);
  position: relative;
  width: 100%;
  &::after {
    content: "";
    position: absolute;
    right: 18px;
    top: 50%;
    width: 9px;
    height: 9px;
    border-right: 2px solid var(--nf-faint);
    border-top: 2px solid var(--nf-faint);
    transform: translateY(-50%) rotate(45deg);
  }
  > b {
    font-size: .95rem;
    font-weight: 600;
    color: var(--nf-ink);
  }
  > span {
    font-size: .78rem;
    color: var(--nf-faint);
  }
  &:hover {
    border-color: var(--nf-outline);
    background: var(--nf-tint);
  }
}

.nf-done-mark {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(110deg, #2E3AE6 0%, #4E5BEE 55%, #6B77F6 100%);
  color: #FFFFFF;
  display: grid;
  place-items: center;
  font-size: 2rem;
  font-weight: 600;
  margin: 8px auto 0;
  box-shadow: 0 14px 28px -14px rgba(46, 58, 230, .6);
}
.nf-body {
  margin: 0;
  color: var(--nf-muted);
  font-size: .88rem;
  line-height: 1.55;
}
.nf-center {
  text-align: center;
}
.nf-phone-big {
  font-size: 1.5rem;
  letter-spacing: .04em;
}
.nf-btn {
  display: block;
  width: 100%;
  border: 0;
  border-radius: 12px;
  padding: 14px;
  font: 600 1rem var(--nf-ui);
  cursor: pointer;
  background: #2E3AE6;
  color: #FFFFFF;
  &:disabled {
    opacity: .6;
    cursor: not-allowed;
  }
}

/* intro screen */
.nf-hero-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: linear-gradient(135deg, #2E3AE6 0%, #4E5BEE 60%, #6B77F6 100%);
  border-radius: 18px;
  padding: 18px 20px;
  color: #FFFFFF;
  > small { font-size: .78rem; opacity: .85; }
  > b { font-size: 1.1rem; font-weight: 700; }
  > span { font-size: .82rem; opacity: .9; line-height: 1.5; }
}
.nf-steps-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  > li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: .93rem;
    color: var(--nf-ink);
    line-height: 1.45;
  }
}
.nf-step-n {
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--nf-blue);
  color: #FFFFFF;
  display: grid;
  place-items: center;
  font-size: .78rem;
  font-weight: 700;
}
.nf-prep {
  background: #FFFFFF;
  border: 1px dashed #B0BBF4;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: .86rem;
  color: var(--nf-muted);
  line-height: 1.6;
  > b { color: var(--nf-ink); }
}

/* form screen */
.nf-pane-form {
  gap: 10px;
  padding-bottom: 40px;
  overflow-y: auto;
}
.nf-sec-title {
  font-size: .82rem;
  font-weight: 600;
  color: var(--nf-muted);
  text-transform: uppercase;
  letter-spacing: .04em;
  border-top: 1px solid var(--nf-line);
  padding-top: 12px;
  margin-top: 6px;
  &:first-child { border-top: none; margin-top: 0; padding-top: 0; }
}
.nf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.nf-label {
  font-size: .88rem;
  font-weight: 500;
  color: var(--nf-ink);
  display: flex;
  align-items: center;
  gap: 4px;
}
.nf-req { color: #E03535; font-weight: 700; }
.nf-opt {
  font-size: .75rem;
  font-weight: 400;
  color: var(--nf-faint);
  background: #F2F4FF;
  border-radius: 6px;
  padding: 1px 6px;
  margin-left: 4px;
}
.nf-input, .nf-select {
  width: 100%;
  font: 400 1rem var(--nf-ui);
  color: var(--nf-ink);
  background: #FFFFFF;
  border: 1px solid #DFDFDF;
  border-radius: 12px;
  padding: 11px 14px;
  min-height: 48px;
  box-sizing: border-box;
  appearance: none;
  &:focus {
    outline: none;
    border-color: #2E3AE6;
    box-shadow: 0 0 0 3px rgba(46,58,230,.16);
  }
  &:disabled {
    background: #F9F9F9;
    border-color: #F2F2F2;
    color: #AFAFAF;
  }
}
.nf-select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236A6A6A' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 34px;
  cursor: pointer;
}
.nf-field-invalid {
  .nf-input, .nf-select { border-color: #E03535; background: #FFF4F4; }
}
.nf-err {
  font-size: .78rem;
  color: #E03535;
  margin: 0;
}
.nf-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: .88rem;
  color: var(--nf-ink);
  cursor: pointer;
  line-height: 1.45;
  > input[type=checkbox] { margin-top: 2px; flex: none; width: 16px; height: 16px; cursor: pointer; accent-color: #2E3AE6; }
}
.nf-sub-field {
  margin-left: 24px;
  background: #F9FBFF;
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid #E8EDFA;
}
.nf-row3 {
  display: grid;
  grid-template-columns: 1fr 1.6fr 1.2fr;
  gap: 8px;
}
.nf-phone-locked {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #F4F6FF;
  border: 1.5px solid #C4CCF5;
  border-radius: 12px;
  padding: 12px 14px;
  > b { font-size: 1.05rem; color: var(--nf-blue); }
}
.nf-verified {
  font-size: .78rem;
  background: #E6FFED;
  color: #1A7A3A;
  border-radius: 8px;
  padding: 3px 8px;
  white-space: nowrap;
}
.nf-help {
  font-size: .8rem;
  color: var(--nf-faint);
  line-height: 1.5;
}
.nf-form-err {
  display: block;
  font-size: .88rem;
  color: #E03535;
  background: #FFF4F4;
  border-radius: 10px;
  padding: 10px 14px;
  text-align: center;
}
.nf-pill {
  display: inline-block;
  font: 600 .78rem var(--nf-ui);
  border-radius: 100px;
  padding: 4px 12px;
  background: #FFF1E0;
  color: #B85C00;
  align-self: flex-start;
}
.nf-kv {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 14px;
  font-size: .86rem;
  background: #FFFFFF;
  border: 1px solid var(--nf-line);
  border-radius: 16px;
  padding: 14px 16px;
  margin: 0;
  > dt { color: var(--nf-muted); white-space: nowrap; }
  > dd { margin: 0; color: var(--nf-ink); font-weight: 500; }
}

.not-found-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 8;
  transition: all 0.5s ease;
  background-color: rgba(255, 255, 255, 0.7);
  > .card-wrapper {
    background: #ffffff;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.16);
    padding: 12px 16px;
    border-radius: 4px;
    > p {
      font-size: 28px;
    }
    > .button-wrapper {
      padding-top: 12px;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
